import React, { useState, useEffect } from 'react';
import { PlusIcon, ArrowUpIcon, ArrowDownIcon, XMarkIcon, MagnifyingGlassIcon, CogIcon } from '@heroicons/react/24/outline';
import api from '../services/api';
import toast from 'react-hot-toast';

export default function Movimentacoes() {
  const [movimentacoes, setMovimentacoes] = useState([]);
  const [itens, setItens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    itemId: '',
    tipo: 'ENTRADA',
    quantidade: '',
    motivo: '',
    observacao: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  
  // Estados para busca de itens
  const [itemSearch, setItemSearch] = useState('');
  const [filteredItens, setFilteredItens] = useState([]);
  const [showItemDropdown, setShowItemDropdown] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  useEffect(() => {
    fetchMovimentacoes();
    fetchItens();
  }, []);

  // Filtrar itens baseado na busca
  useEffect(() => {
    if (itemSearch.trim() && !selectedItem) {
      const filtered = itens.filter(item => 
        item.nome.toLowerCase().includes(itemSearch.toLowerCase()) ||
        item.codigo.toLowerCase().includes(itemSearch.toLowerCase())
      );
      setFilteredItens(filtered);
      setShowItemDropdown(true);
    } else {
      setFilteredItens([]);
      setShowItemDropdown(false);
    }
  }, [itemSearch, itens, selectedItem]);

  const fetchMovimentacoes = async () => {
    try {
      const response = await api.get('/movimentacoes');
      setMovimentacoes(response.data.movimentacoes);
    } catch (error) {
      console.error('Erro ao carregar movimentações:', error);
      toast.error('Erro ao carregar movimentações');
    } finally {
      setLoading(false);
    }
  };

  const fetchItens = async () => {
    try {
      // Buscar TODOS os itens ativos sem paginação para seleção
      const response = await api.get('/itens?ativo=true&limit=10000');
      setItens(response.data.itens || response.data);
    } catch (error) {
      console.error('Erro ao carregar itens:', error);
    }
  };

  const openModal = () => {
    setFormData({
      itemId: '',
      tipo: 'ENTRADA',
      quantidade: '',
      motivo: '',
      observacao: ''
    });
    setFormErrors({});
    setItemSearch('');
    setSelectedItem(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setFormErrors({});
    setItemSearch('');
    setSelectedItem(null);
    setShowItemDropdown(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleItemSearch = (e) => {
    const value = e.target.value;
    setItemSearch(value);
    setFormData(prev => ({ ...prev, itemId: '' }));
    setSelectedItem(null);
    setShowItemDropdown(false);
  };

  const selectItem = (item) => {
    setSelectedItem(item);
    setFormData(prev => ({ ...prev, itemId: item.id }));
    setItemSearch(`${item.nome} (${item.codigo})`);
    setShowItemDropdown(false);
    setFilteredItens([]);
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.itemId) errors.itemId = 'Selecione o item';
    if (!formData.tipo) errors.tipo = 'Selecione o tipo';
    if (!formData.quantidade || isNaN(formData.quantidade) || Number(formData.quantidade) <= 0) errors.quantidade = 'Informe uma quantidade válida';
    if (!formData.motivo.trim()) errors.motivo = 'Motivo é obrigatório';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSubmitting(true);
    try {
      await api.post('/movimentacoes', {
        ...formData,
        quantidade: Number(formData.quantidade)
      });
      toast.success('Movimentação registrada com sucesso!');
      closeModal();
      fetchMovimentacoes();
    } catch (error) {
      console.error('Erro ao registrar movimentação:', error);
      const message = error.response?.data?.error || 'Erro ao registrar movimentação';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Movimentações</h1>
          <p className="mt-1 text-sm text-gray-500">
            Controle de entrada e saída de estoque
          </p>
        </div>
        <button className="btn-primary" onClick={openModal}>
          <PlusIcon className="h-5 w-5 mr-2" />
          Nova Movimentação
        </button>
      </div>

      <div className="card">
        <div className="card-body">
          <div className="flow-root">
            <ul className="-my-5 divide-y divide-gray-200">
              {movimentacoes.map((movimentacao) => (
                <li key={movimentacao.id} className="py-4">
                  <div className="flex items-center space-x-4">
                    <div className="flex-shrink-0">
                      {movimentacao.tipo === 'ENTRADA' ? (
                        <ArrowUpIcon className="h-5 w-5 text-success-600" />
                      ) : movimentacao.tipo === 'AJUSTE' ? (
                        <CogIcon className="h-5 w-5 text-blue-600" />
                      ) : (
                        <ArrowDownIcon className="h-5 w-5 text-danger-600" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {movimentacao.item.nome}
                      </p>
                      <p className="text-sm text-gray-500">
                        {movimentacao.motivo} • {movimentacao.user.name}
                      </p>
                    </div>
                    <div className="flex-shrink-0 text-sm text-gray-500">
                      {new Date(movimentacao.data).toLocaleDateString('pt-BR')}
                    </div>
                    <div className="flex-shrink-0">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        movimentacao.tipo === 'ENTRADA' 
                          ? 'bg-success-100 text-success-800'
                          : movimentacao.tipo === 'AJUSTE'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-danger-100 text-danger-800'
                      }`}>
                        {movimentacao.quantidade} {movimentacao.item.unidade}
                      </span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Modal de Cadastro de Movimentação */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-2/3 lg:w-1/3 shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Nova Movimentação
              </h3>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Busca de Item */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Item *
                </label>
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    value={itemSearch}
                    onChange={handleItemSearch}
                    className={`input pl-10 ${formErrors.itemId ? 'border-red-500' : ''}`}
                    placeholder="Digite o nome ou código do item..."
                  />
                  {showItemDropdown && filteredItens.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                      {filteredItens.map((item) => (
                        <div
                          key={item.id}
                          onClick={() => selectItem(item)}
                          className="px-4 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                        >
                          <div className="font-medium text-gray-900">{item.nome}</div>
                          <div className="text-sm text-gray-500">{item.codigo} • Estoque: {item.estoqueAtual} {item.unidade}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  {showItemDropdown && filteredItens.length === 0 && itemSearch.trim() && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
                      <div className="px-4 py-2 text-gray-500">Nenhum item encontrado</div>
                    </div>
                  )}
                </div>
                {formErrors.itemId && (
                  <p className="text-red-500 text-xs mt-1">{formErrors.itemId}</p>
                )}
                {selectedItem && (
                  <div className="mt-2 p-2 bg-gray-50 rounded-md">
                    <div className="text-sm">
                      <span className="font-medium">Item selecionado:</span> {selectedItem.nome} ({selectedItem.codigo})
                    </div>
                    <div className="text-xs text-gray-500">
                      Estoque atual: {selectedItem.estoqueAtual} {selectedItem.unidade}
                    </div>
                  </div>
                )}
              </div>

              {/* Tipo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo *
                </label>
                <select
                  name="tipo"
                  value={formData.tipo}
                  onChange={handleInputChange}
                  className={`input ${formErrors.tipo ? 'border-red-500' : ''}`}
                >
                  <option value="ENTRADA">Entrada</option>
                  <option value="SAIDA">Saída</option>
                  <option value="AJUSTE">Ajuste</option>
                </select>
                {formErrors.tipo && (
                  <p className="text-red-500 text-xs mt-1">{formErrors.tipo}</p>
                )}
              </div>

              {/* Quantidade */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Quantidade *
                </label>
                <input
                  type="number"
                  name="quantidade"
                  value={formData.quantidade}
                  onChange={handleInputChange}
                  min="0.01"
                  step="0.01"
                  className={`input ${formErrors.quantidade ? 'border-red-500' : ''}`}
                  placeholder="Quantidade"
                />
                {formErrors.quantidade && (
                  <p className="text-red-500 text-xs mt-1">{formErrors.quantidade}</p>
                )}
              </div>

              {/* Motivo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Motivo *
                </label>
                <input
                  type="text"
                  name="motivo"
                  value={formData.motivo}
                  onChange={handleInputChange}
                  className={`input ${formErrors.motivo ? 'border-red-500' : ''}`}
                  placeholder="Motivo da movimentação"
                />
                {formErrors.motivo && (
                  <p className="text-red-500 text-xs mt-1">{formErrors.motivo}</p>
                )}
              </div>

              {/* Observação */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Observação
                </label>
                <textarea
                  name="observacao"
                  value={formData.observacao}
                  onChange={handleInputChange}
                  rows="2"
                  className="input"
                  placeholder="Observações adicionais..."
                />
              </div>

              {/* Botões */}
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="btn-secondary"
                  disabled={submitting}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={submitting}
                >
                  {submitting ? 'Salvando...' : 'Registrar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
} 